import { z } from 'zod'
import type { MyOrgIntern } from '@/entities/org-intern/model/myOrgIntern'
import { calendarDay, todayCalendarDay } from '@/shared/lib/datetime/calendarDates'

export const myOrgInternSearchSchema = z.object({
  search: z.string().catch(''),
  status: z.enum(['all', 'ongoing', 'upcoming', 'ended']).catch('all'),
  eduOrgId: z.string().catch(''),
  internshipId: z.string().catch(''),
  page: z.coerce.number().int().positive().catch(1),
  pageSize: z.coerce
    .number()
    .pipe(z.union([z.literal(10), z.literal(25), z.literal(50)]))
    .catch(25),
  sortBy: z.enum(['intern', 'eduOrg', 'internship', 'date']).catch('intern'),
  direction: z.enum(['asc', 'desc']).catch('asc'),
})
export type MyOrgInternSearch = z.infer<typeof myOrgInternSearchSchema>
export const defaultMyOrgInternSearch = myOrgInternSearchSchema.parse({})

export function selectMyOrgInterns(
  rows: MyOrgIntern[],
  search: MyOrgInternSearch,
  today = todayCalendarDay()
) {
  return rows
    .filter((row) => {
      const placement = row.placement
      if (search.eduOrgId && placement?.eduOrgId !== search.eduOrgId) {
        return false
      }
      if (search.internshipId && placement?.internshipId !== search.internshipId) {
        return false
      }
      if (search.status !== 'all') {
        if (!placement?.start || !placement.end) {
          return false
        }
        const start = calendarDay(placement.start)
        const end = calendarDay(placement.end)
        if (search.status === 'ongoing' && !(start <= today && end >= today)) {
          return false
        }
        if (search.status === 'upcoming' && !(start > today)) {
          return false
        }
        if (search.status === 'ended' && !(end < today)) {
          return false
        }
      }
      return true
    })
    .toSorted((a, b) => {
      const value = (row: MyOrgIntern) => {
        if (search.sortBy === 'intern') {
          return row.displayName
        }
        if (search.sortBy === 'eduOrg') {
          return row.placement?.eduOrg ?? ''
        }
        if (search.sortBy === 'internship') {
          return row.placement?.internship ?? ''
        }
        return row.placement?.end ?? ''
      }
      return (
        value(a).localeCompare(value(b), 'lv', { sensitivity: 'base' }) *
          (search.direction === 'desc' ? -1 : 1) || a.id.localeCompare(b.id)
      )
    })
}
